import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Heart, Clock, Play, BookOpen } from "lucide-react";
import { TECHNIQUES, POSTURE_LABEL, intensityColor, intensityLabelKey } from "./breathTechniques";

export default function BreathworkTechniques() {
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("breathwork_techniques_title"));
  const [selected, setSelected] = useState(null);
  const [favs, setFavs] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const loadFavs = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites", { params: { kind: "technique" } });
      setFavs(new Set((Array.isArray(data) ? data : []).map((r) => r.item_key)));
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  const toggleFav = async (e, key) => {
    e.stopPropagation();
    const isFav = favs.has(key);
    setFavs((prev) => { const n = new Set(prev); if (isFav) n.delete(key); else n.add(key); return n; });
    try { await api.post("/favorites/toggle", { kind: "technique", item_key: key }); }
    catch { setFavs((prev) => { const n = new Set(prev); if (isFav) n.add(key); else n.delete(key); return n; }); }
  };

  const steps = (tech) => {
    const out = [];
    for (let i = 1; i <= tech.stepCount; i++) {
      const k = `tech_${tech.key}_step${i}`;
      const v = t(k);
      if (v && v !== k) out.push(v);
    }
    return out;
  };

  const start = (tech) => {
    setSelected(null);
    if (tech.key === "connected") { toast.message(t("tech_practice_guide_only"), { description: t("tech_practice_guide_only_body") }); return; }
    if (tech.techniqueId) navigate(`/app/breathwork/session/${tech.key}`);
    else toast.message(t("tech_practice_guide_only"), { description: t("tech_practice_guide_only_body") });
  };

  return (
    <div className="space-y-4" data-testid="breathwork-techniques">
      <button onClick={() => navigate("/app/breathwork")} data-testid="tech-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t("breathwork")}
      </button>
      <div>
        <h1 className="font-display text-2xl text-foreground">{t("breathwork_techniques_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("breathwork_techniques_sub")}</p>
      </div>

      {loading ? <Spinner label={t("common.loading")} /> : (
        <div className="grid gap-3 sm:grid-cols-2">
          {TECHNIQUES.map((tech) => {
            const favored = favs.has(tech.key);
            const col = intensityColor(tech.intensity);
            return (
              <button key={tech.key} onClick={() => setSelected(tech)} data-testid={`tech-${tech.key}`}
                className="rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent/30">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-display text-base text-foreground">{t(`tech_${tech.key}`)}</span>
                  <span onClick={(e) => toggleFav(e, tech.key)} data-testid={`heart-tech-${tech.key}`} className="shrink-0 rounded-lg p-1">
                    <Heart className="h-5 w-5" fill={favored ? "#B4553F" : "none"} style={{ color: favored ? "#B4553F" : "#9CA3AF" }} />
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{t(`tech_${tech.key}_desc`)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{tech.duration}</span>
                  <span className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: col, borderColor: col, backgroundColor: col + "1A" }}>{t(intensityLabelKey(tech.intensity))}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="tech-detail-dialog">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle>{t(`tech_${selected.key}`)}</DialogTitle>
                <DialogDescription className="sr-only">{t(`tech_${selected.key}_desc`)}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] space-y-4 overflow-y-auto">
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">{t("recommended_posture")}</p>
                  <p className="mt-0.5 text-sm text-foreground">{t(POSTURE_LABEL[selected.posture])}</p>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">{t(`tech_${selected.key}_desc`)}</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl border border-border bg-card p-2.5"><p className="text-[10px] uppercase text-muted-foreground">{t("duration_2")}</p><p className="mt-0.5 text-sm font-semibold text-foreground">{selected.duration}</p></div>
                  <div className="rounded-xl border border-border bg-card p-2.5"><p className="text-[10px] uppercase text-muted-foreground">{t("intensity_2")}</p><p className="mt-0.5 text-sm font-semibold" style={{ color: intensityColor(selected.intensity) }}>{t(intensityLabelKey(selected.intensity))}</p></div>
                  <div className="rounded-xl border border-border bg-card p-2.5"><p className="text-[10px] uppercase text-muted-foreground">{t("best_for")}</p><p className="mt-0.5 line-clamp-2 text-xs text-foreground">{t(`tech_${selected.key}_bestfor`)}</p></div>
                </div>
                <div>
                  <p className="mb-2 font-display text-sm text-foreground">{t("how_to")}</p>
                  {steps(selected).map((line, idx) => {
                    const isSafety = selected.safetyStepIndex && idx + 1 === selected.safetyStepIndex;
                    return (
                      <div key={idx} className={`flex items-start gap-2.5 py-1.5 ${isSafety ? "rounded-lg bg-rose-50 px-2" : ""}`}>
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${isSafety ? "bg-rose-600 text-white" : "bg-primary/15 text-primary"}`}>{idx + 1}</span>
                        <p className={`text-sm ${isSafety ? "font-medium text-rose-700" : "text-foreground"}`}>{line}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <Button onClick={() => start(selected)} data-testid="tech-start-session" className="w-full gap-2 rounded-full">
                {selected.key === "connected" ? <BookOpen className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {selected.key === "connected" ? t("tech_connected_learn_more") : selected.techniqueId ? t("breathwork_start_title") : t("tech_practice_guide_only")}
              </Button>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
